import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Task, CreateTaskForm } from "@/lib/schemas/task";

export function useBulkUpdateTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { ids: string[]; updates: Partial<CreateTaskForm> }) => {
      const supabase = getSupabase();
      const { ids, updates } = params;

      const updateData: Partial<Task> = { ...updates };
      if (updateData.status === "completed") {
        updateData.completed_at = new Date().toISOString();
      }
      if (updateData.status && updateData.status !== "completed") {
        updateData.completed_at = null;
      }

      const { data, error } = await supabase
        .from("contact_tasks")
        .update(updateData as any)
        .in("id", ids)
        .select();

      if (error) {
        console.error("Error bulk updating tasks:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      toast.success(`Successfully updated ${data.length} tasks`);
    },
    onError: (error: Error) => {
      console.error("Error bulk updating tasks:", error);
      toast.error("Failed to update tasks: " + error.message);
    },
  });
}

export function useBulkDeleteTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_tasks").delete().in("id", ids);

      if (error) {
        console.error("Error bulk deleting tasks:", error);
        throw new Error(error.message);
      }

      return ids;
    },
    onSuccess: (ids) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks-paginated"] });
      toast.success(`Successfully deleted ${ids.length} tasks`);
    },
    onError: (error: Error) => {
      console.error("Error bulk deleting tasks:", error);
      toast.error("Failed to delete tasks: " + error.message);
    },
  });
}
